import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../../AuthContext';
import { useSubAgentAuth } from '../SubAgent/SubAgentAuthContext';
import StudentPreferences from '../../../components/StudentPreferences';
import InitiateServices from '../../../components/InitiateServices';
import RequestDocument from '../../../components/RequestDocument';
import Tasks from '../../../components/Tasks';
import PersonalDetails from '../../../components/PersonalDetails';
import AcademicResults from '../../../components/AcademicResults';
import TestResults from '../../../components/TestResults';
import UploadedDocuments from '../../../components/UploadedDocuments';
import CallLogs from '../../../components/CallLogs';
import FollowUps from '../../../components/FollowUps';
import AdmissionDetails from '../../../components/AdmissionDetails';
import MyTasks from '../../../components/MyTasks';
import AppliedUniversities from '../../../components/AppliedUniversities';
import Registrations from '../../../components/Registrations';
import UniversityPayment from '../../../components/UniversityPayment';

interface SubmittedDocument {
    name: string;
    file: File;
    uploadTime: Date;
    iconPath: string;
}

const StudentDetails = () => {

    const { departmentData } = useAuth();

    const { subAgentPermissions } = useSubAgentAuth();

    // Sections array
    let sectionsArray: string[] = [];
    if (departmentData) {
        sectionsArray = Object.values(Object.values(departmentData?.tabPermissions))
            .filter(permission => permission.read === true)
            .map(permission => permission.name) || [];
    } else if (subAgentPermissions) {
        sectionsArray = subAgentPermissions?.tabPermissions
            .filter((permission: { read: boolean; }) => permission.read === true)
            .map((permission: { name: string; }) => permission.name) || [];
    }

    const getWritePermission = (section: string) => {
        if (departmentData) {
            return Object.values(departmentData?.tabPermissions)
                .find(permission => permission.name === section)?.write || false;
        } else if (subAgentPermissions) {
            return subAgentPermissions?.tabPermissions
                .find((permission: { name: string; }) => permission.name === section)?.write || false;
        }
        return false;
    }

    // Track expanded sections with a state object where keys are section names and values are boolean
    const [expandedSections, setExpandedSections] = useState<{ [key: string]: boolean }>({
        "Initiate Services": false,
        "Admission": false,
        "Complete Profile": false,
        "Tasks": false,
        "History": false,
        "Registration": false,
        "University Payout": false,
    });

    // Sections array with icons and descriptions
    const sections = [
        // { id: "Student Attributes", title: "Student Attributes", icon: "👤", description: "View and edit student preferences", color: "#FFD6E0" }, // Soft pink
        { id: "Complete Profile", title: "Complete Profile", icon: "✅", description: "Complete student profile information", color: "#FFE5B4" }, // Peach
        { id: "Initiate Services", title: "Initiate Services", icon: "🚀", description: "Start new services for this student", color: "#C1E1C1" }, // Mint green
        // { id: "Request Document", title: "Request Document", icon: "📄", description: "Request documents from the student", color: "#C4DDFF" }, // Light blue
        // { id: "Admission Details", title: "Admission Details", icon: "🎓", description: "View admission status and details", color: "#FFF9C4" }, // Lemon chiffon
        { id: "Admission", title: "Admission", icon: "🎓", description: "View admission status and details", color: "#FFF9C4" }, // Lemon chiffon
        // { id: "Applied Universities", title: "Applied Universities", icon: "🏛️", description: "View universities applied to", color: "#E6E6FA" }, // Lavender
        { id: "Tasks", title: "Tasks", icon: "📋", description: "View and manage student tasks", color: "#D4F0F0" }, // Light cyan
        // { id: "Student Tasks", title: "Student Tasks", icon: "📋", description: "View and manage student tasks", color: "#D4F0F0" }, // Light cyan
        // { id: "My Tasks", title: "My Tasks", icon: "📌", description: "Your tasks related to this student", color: "#E0F7FA" }, // Pale cyan
        { id: "History", title: "History", icon: "📊", description: "Call logs and follow-ups history", color: "#F1E8FF" }, // Pale lavender

        { id: "Registration", title: "Registration", icon: "📝", description: "View and manage student Registration", color: "#D4EDDA" }, // Light green

        { id: "University Payout", title: "University Payout", icon: "📝", description: "View and manage student UniversityPayment", color: "#D4EDDA" },

    ];

    const progressSteps = [
        { label: "Student Preferences", status: "completed" },
        { label: "Personal Details", status: "completed" },
        { label: "Academic Results", status: "active" },
        { label: "Test Results", status: "pending" },
        { label: "Uploaded Documents", status: "pending" }
    ];
    const admissionProgressSteps = [
        { label: "Applied Universities", status: "completed" },
        { label: "Admission Details", status: "active" },
        { label: "Upload Documents", status: "pending" },
    ];
    const taskProgressSteps = [
        { label: "My Tasks", status: "pending" },
        { label: "Student Tasks", status: "active" },
    ];
    const [activeProfileStep, setActiveProfileStep] = useState("Student Preferences");
    const [activeAdmissionStep, setActiveAdmissionStep] = useState("Applied Universities");
    const [activeTaskStep, setActiveTaskStep] = useState("My Tasks");

    // Handler to toggle section expansion
    const toggleSection = (sectionId: string) => {

        console.log('Toggling section:', sectionId); // Debug log

        setExpandedSections(prev => ({
            ...prev,
            [sectionId]: !prev[sectionId]
        }));
    };

    // Custom SVG icons for each section
    const SectionIcons = {
        studentAttributes: (
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 11C14.2091 11 16 9.20914 16 7C16 4.79086 14.2091 3 12 3C9.79086 3 8 4.79086 8 7C8 9.20914 9.79086 11 12 11Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                <path d="M6 21V19C6 17.9391 6.42143 16.9217 7.17157 16.1716C7.92172 15.4214 8.93913 15 10 15H14C15.0609 15 16.0783 15.4214 16.8284 16.1716C17.5786 16.9217 18 17.9391 18 19V21" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
        ),
        initiateServices: (
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M21 13.5V6.5C21 5.83696 20.7366 5.20107 20.2678 4.73223C19.7989 4.26339 19.163 4 18.5 4H5.5C4.83696 4 4.20107 4.26339 3.73223 4.73223C3.26339 5.20107 3 5.83696 3 6.5V18.5C3 19.163 3.26339 19.7989 3.73223 20.2678C4.20107 20.7366 4.83696 21 5.5 21H13.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                <path d="M3 6.5L12 12.5L21 6.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                <path d="M17 17L19 19L23 15" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
        ),
        requestDocument: (
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M14 3V7C14 7.26522 14.1054 7.51957 14.2929 7.70711C14.4804 7.89464 14.7348 8 15 8H19" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                <path d="M17 21H7C6.46957 21 5.96086 20.7893 5.58579 20.4142C5.21071 20.0391 5 19.5304 5 19V5C5 4.46957 5.21071 3.96086 5.58579 3.58579C5.96086 3.21071 6.46957 3 7 3H14L19 8V19C19 19.5304 18.7893 20.0391 18.4142 20.4142C18.0391 20.7893 17.5304 21 17 21Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                <path d="M9 9H10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                <path d="M9 13H15" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                <path d="M9 17H15" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
        ),
        admissionDetails: (
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 15C15.866 15 19 11.866 19 8C19 4.13401 15.866 1 12 1C8.13401 1 5 4.13401 5 8C5 11.866 8.13401 15 12 15Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                <path d="M8.21 13.89L7 23L12 20L17 23L15.79 13.88" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
        ),
        appliedUniversities: (
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M3 21H21" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                <path d="M5 21V7L13 3V21" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                <path d="M19 21V10L13 7" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                <path d="M9 9V9.01" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                <path d="M9 12V12.01" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                <path d="M9 15V15.01" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                <path d="M9 18V18.01" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
        ),
        completeProfile: (
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M16 18L18 20L22 16" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                <path d="M12 15H8C6.13623 15 4.29199 15.7375 2.92857 17.0179C1.56516 18.2984 0.999565 20 1 21.75C1.00043 22.0265 1.05516 22.3 1.16089 22.5521C1.26662 22.8042 1.42144 23.0285 1.61429 23.2089C1.80714 23.3893 2.03399 23.5218 2.28099 23.6083C2.528 23.6948 2.7907 23.734 3.05714 23.7321H12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                <path d="M10 11C12.7614 11 15 8.76142 15 6C15 3.23858 12.7614 1 10 1C7.23858 1 5 3.23858 5 6C5 8.76142 7.23858 11 10 11Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
        ),
        studentTasks: (
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M9 11L12 14L20 6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                <path d="M20 12V18C20 18.5304 19.7893 19.0391 19.4142 19.4142C19.0391 19.7893 18.5304 20 18 20H6C5.46957 20 4.96086 19.7893 4.58579 19.4142C4.21071 19.0391 4 18.5304 4 18V6C4 5.46957 4.21071 4.96086 4.58579 4.58579C4.96086 4.21071 5.46957 4 6 4H16" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
        ),
        myTasks: (
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 8V12L15 15" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                <path d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
        ),
        history: (
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 8V12L14 14" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                <path d="M3.05078 11.0002C3.27441 8.18939 4.51578 5.72313 6.39777 3.90092C8.27976 2.0787 10.6329 1.02524 13.0636 0.965364C15.4943 0.905489 17.8669 1.84346 19.8024 3.58678C21.738 5.3301 22.9939 7.74211 23.2388 10.3498C23.4837 12.9574 22.7059 15.5529 21.0656 17.5731C19.4253 19.5934 17.0524 20.8718 14.45 21.1667C11.8476 21.4617 9.2435 20.7486 7.16748 19.1819" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                <path d="M3 4V11H10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
        ),
        Registration: (
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M19 11H5C3.89543 11 3 11.8954 3 13V19C3 20.1046 3.89543 21 5 21H19C20.1046 21 21 20.1046 21 19V13C21 11.8954 20.1046 11 19 11Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                <path d="M7 11V7C7 5.67392 7.52678 4.40215 8.46447 3.46447C9.40215 2.52678 10.6739 2 12 2C13.3261 2 14.5979 2.52678 15.5355 3.46447C16.4732 4.40215 17 5.67392 17 7V11" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
        ),
        UniversityPayment: (
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M19 11H5C3.89543 11 3 11.8954 3 13V19C3 20.1046 3.89543 21 5 21H19C20.1046 21 21 20.1046 21 19V13C21 11.8954 20.1046 11 19 11Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                <path d="M7 11V7C7 5.67392 7.52678 4.40215 8.46447 3.46447C9.40215 2.52678 10.6739 2 12 2C13.3261 2 14.5979 2.52678 15.5355 3.46447C16.4732 4.40215 17 5.67392 17 7V11" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
        ),
    };

    // Custom CSS animations and advanced styling
    const customAnimations = `
@keyframes slideDown {
    from { max-height: 0; opacity: 0; transform: translateY(-10px); }
    to { max-height: 2000px; opacity: 1; transform: translateY(0); }
}

@keyframes slideUp {
    from { max-height: 2000px; opacity: 1; transform: translateY(0); }
    to { max-height: 0; opacity: 0; transform: translateY(-10px); }
}

@keyframes fadeIn {
    from { opacity: 0; transform: translateY(-10px); }
    to { opacity: 1; transform: translateY(0); }
}

@keyframes pulseHighlight {
    0% { box-shadow: 0 0 0 0 rgba(59, 130, 246, 0.5); }
    70% { box-shadow: 0 0 0 5px rgba(59, 130, 246, 0); }
    100% { box-shadow: 0 0 0 0 rgba(59, 130, 246, 0); }
}

@keyframes shimmer {
    0% { background-position: -468px 0; }
    100% { background-position: 468px 0; }
}

@keyframes float {
    0% { transform: translateY(0px); }
    50% { transform: translateY(-5px); }
    100% { transform: translateY(0px); }
}

@keyframes colorPulse {
    0% { color: #3B82F6; }
    50% { color: #1E40AF; }
    100% { color: #3B82F6; }
}

@keyframes rotateIn {
    from { transform: rotateX(-90deg); opacity: 0; }
    to { transform: rotateX(0); opacity: 1; }
}

/* Simplified animation states */
.section-content-enter {
    display: block;
    animation: fadeIn 0.3s ease-out forwards;
}

.section-content-exit {
    display: none;
}

.section-header-active {
    box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
}

.section-header-hover:hover {
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
}

.section-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 40px;
    height: 40px;
    border-radius: 12px;
    background-color: #F3F4F6;
    color: #4B5563;
    transition: all 0.3s ease;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
}

.section-header-hover:hover .section-icon {
    transform: translateY(-2px);
    box-shadow: 0 3px 6px rgba(0, 0, 0, 0.1);
}

.section-header-active .section-icon {
    background: linear-gradient(135deg, #DBEAFE 0%, #93C5FD 100%);
    color: #1E40AF;
    box-shadow: 0 2px 4px rgba(59, 130, 246, 0.3);
}

.section-arrow {
    transition: transform 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275);
}

.section-arrow-up {
    transform: rotate(180deg);
}

.pulse-animation {
    animation: pulseHighlight 2s infinite;
}

.float-animation {
    animation: float 3s ease-in-out infinite;
}

.color-pulse-animation {
    animation: colorPulse 2s ease-in-out infinite;
}

.fade-in-animation {
    animation: fadeIn 0.5s ease-out forwards;
}

.rotate-in-animation {
    animation: rotateIn 0.5s cubic-bezier(0.175, 0.885, 0.32, 1.275) forwards;
}

.fancy-border {
    position: relative;
    border-radius: 12px;
    overflow: hidden;
}

.fancy-border::after {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: 12px; 
    padding: 1px;
    background: linear-gradient(135deg, #60A5FA, #3B82F6, #1E40AF);
    -webkit-mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
    -webkit-mask-composite: xor;
    mask-composite: exclude;
    opacity: 0;
    transition: opacity 0.3s ease;
    pointer-events: none; /* Allow clicks to pass through */
    z-index: 0;
}

.fancy-border:hover::after {
    opacity: 1;
}

.shimmer {
    background: linear-gradient(to right, rgba(255,255,255,0) 0%, rgba(255,255,255,0.2) 20%, rgba(255,255,255,0) 40%);    
    background-size: 940px 100%;
    animation: shimmer 1.5s infinite linear;
}

.progress-border-gradient {
    position: relative;
}

.progress-border-gradient::before {
    content: '';
    position: absolute;
    top: -2px;
    left: -2px;
    right: -2px;
    bottom: -2px;
    border-radius: 14px;
    background: linear-gradient(135deg, #60A5FA, #3B82F6, #1E40AF);
    z-index: -1;
    pointer-events: none; /* Allow clicks to pass through */
}

.box-shadow-strong {
    box-shadow: 0 10px 25px -5px rgba(59, 130, 246, 0.1), 0 8px 10px -6px rgba(59, 130, 246, 0.05);
}

.box-shadow-blue {
    box-shadow: 0 4px 14px rgba(59, 130, 246, 0.2);
}
`;

    return (
        <div>
            <style>{customAnimations}</style>

            {/* Accordion-style navigation */}
            <div className="space-y-4 mb-4">
                {sections
                    .filter(section => sectionsArray.includes(section.id))
                    .map((section) => {
                        // Determine which SVG icon to use based on section ID
                        let iconComponent;
                        switch (section.id) {
                            case "Initiate Services": iconComponent = SectionIcons.initiateServices; break;
                            case "Admission": iconComponent = SectionIcons.admissionDetails; break;
                            case "Complete Profile": iconComponent = SectionIcons.completeProfile; break;
                            case "Tasks": iconComponent = SectionIcons.studentTasks; break;
                            case "History": iconComponent = SectionIcons.history; break;
                            case "Registration": iconComponent = SectionIcons.Registration; break;
                            case "University Payout": iconComponent = SectionIcons.UniversityPayment; break;
                            default: iconComponent = null;
                        }

                        return (
                            <div
                                key={section.id}
                                className={`border border-gray-200 rounded-xl overflow-hidden transition-all duration-300 shadow-sm hover:shadow-md ${expandedSections[section.id] ? 'box-shadow-blue' : ''
                                    }`}
                                style={{ borderLeft: `4px solid ${section.color}` }}
                            >
                                {/* Section Header */}
                                <div
                                    className={`flex items-center justify-between p-4 cursor-pointer transition-colors duration-300
                                            ${expandedSections[section.id] ? 'section-header-active' : 'section-header-hover'}`}
                                    style={{
                                        background: expandedSections[section.id]
                                            ? `linear-gradient(135deg, ${section.color}90, ${section.color})`
                                            : `linear-gradient(135deg, white, ${section.color}10)`,
                                        transition: 'background 0.3s ease'
                                    }}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        toggleSection(section.id);
                                    }}
                                >
                                    <div className="flex items-center space-x-4">
                                        <div
                                            className="section-icon"
                                            style={{
                                                background: expandedSections[section.id] ? 'white' : `${section.color}50`,
                                                color: '#4B5563',
                                                boxShadow: expandedSections[section.id] ? `0 0 0 4px ${section.color}30` : 'none'
                                            }}
                                        >
                                            {iconComponent}
                                        </div>
                                        <div>
                                            <h3 className="font-medium" style={{ color: '#1F2937' }}>{section.title}</h3>
                                            <p className="text-xs" style={{ color: '#6B7280' }}>{section.description}</p>
                                        </div>
                                    </div>
                                    <div className={`w-6 h-6 flex items-center justify-center section-arrow ${expandedSections[section.id] ? 'section-arrow-up' : ''}`} style={{ color: expandedSections[section.id] ? 'white' : '#6B7280' }}>
                                        <svg width="14" height="8" viewBox="0 0 14 8" fill="none" xmlns="http://www.w3.org/2000/svg">
                                            <path d="M1 1L7 7L13 1" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                        </svg>
                                    </div>
                                </div>

                                {/* Section Content */}
                                <div
                                    className={expandedSections[section.id] ? 'section-content-enter' : 'section-content-exit'}
                                >
                                    <div className="border-t border-white" style={{ background: `${section.color}20` }}>
                                        <div className="p-6">

                                            {/* Initiate Services Section */}
                                            {section.id === "Initiate Services" && (
                                                <InitiateServices write={getWritePermission('Initiate Services')} />
                                            )}

                                            {section.id === "Admission" && (
                                                <div className="complete-profile-section ml-12">
                                                    {/* Progress Steps Bar */}
                                                    <div className="relative flex w-full">
                                                        <div className="relative w-full max-w-3xl">
                                                            <div className="absolute top-2 left-[5%] right-[5%] border-t-2 border-dotted border-[#3483BE]" />
                                                            <div className="relative flex justify-between w-full">
                                                                {admissionProgressSteps.map((step, index) => (
                                                                    <div
                                                                        key={index}
                                                                        className="flex flex-col items-center  cursor-pointer"
                                                                        onClick={() => setActiveAdmissionStep(step.label)}
                                                                    >
                                                                        <div
                                                                            className={`w-5 h-5 rounded-full border-2 flex items-center justify-center 
                                                                               ${step.label === activeAdmissionStep
                                                                                    ? "border-[#3483BE] bg-white"
                                                                                    : index < admissionProgressSteps.findIndex(s => s.label === activeAdmissionStep)
                                                                                        ? "border-[#3483BE] bg-[#3483BE]"
                                                                                        : "border-[#3483BE] bg-white"
                                                                                }`}
                                                                        >
                                                                            {step.label === activeAdmissionStep && (
                                                                                <div className="w-3 h-3 rounded-full bg-gradient-to-t from-[#3483BE] to-[#02385E]"></div>
                                                                            )}
                                                                        </div>
                                                                        <span className="text-xs mt-2 text-center w-24 text-gray-600">{step.label}</span>
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        </div>
                                                    </div>
                                                    {/* Content for each step */}
                                                    <div className="mt-8">
                                                        {activeAdmissionStep === "Applied Universities" && (
                                                            <AppliedUniversities write={getWritePermission("Admission")} />
                                                        )}
                                                        {activeAdmissionStep === "Upload Documents" && (
                                                            <div className="request-doc-section">
                                                                <RequestDocument write={getWritePermission("Admission")} />
                                                            </div>
                                                        )}

                                                        {activeAdmissionStep === "Admission Details" && (
                                                            <AdmissionDetails write={getWritePermission("Admission")} />

                                                        )}
                                                    </div>
                                                </div>
                                            )}

                                            {/* Complete Profile Section */}
                                            {section.id === "Complete Profile" && (
                                                <div className="complete-profile-section">
                                                    {/* Progress Steps Bar */}
                                                    <div className="relative flex w-full">
                                                        <div className="relative w-full max-w-3xl">
                                                            <div className="absolute top-2 left-[5%] right-[5%] border-t-2 border-dotted border-[#3483BE] shimmer" />
                                                            <div className="relative flex justify-between w-full fade-in-animation" style={{ marginBottom: '20px' }}>
                                                                {progressSteps.map((step, index) => (
                                                                    <div
                                                                        key={index}
                                                                        className="flex flex-col items-center cursor-pointer"
                                                                        onClick={() => setActiveProfileStep(step.label)}
                                                                    >
                                                                        <div
                                                                            className={`w-5 h-5 rounded-full border-2 flex items-center justify-center 
                                                                        ${step.label === activeProfileStep
                                                                                    ? "border-[#3483BE] bg-white"
                                                                                    : index < progressSteps.findIndex(s => s.label === activeProfileStep)
                                                                                        ? "border-[#3483BE] bg-[#3483BE]"
                                                                                        : "border-[#3483BE] bg-white"
                                                                                }`}
                                                                        >
                                                                            {step.label === activeProfileStep && (
                                                                                <div className="w-3 h-3 rounded-full bg-gradient-to-t from-[#3483BE] to-[#02385E]"></div>
                                                                            )}
                                                                        </div>
                                                                        <span className="text-xs mt-2 text-center w-24 text-gray-600">{step.label}</span>
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        </div>
                                                    </div>

                                                    {/* Content for each step */}
                                                    <div className="mt-8">
                                                        {activeProfileStep === "Student Preferences" && (
                                                            <StudentPreferences write={getWritePermission("Complete Profile")} />
                                                        )}
                                                        {activeProfileStep === "Personal Details" && (
                                                            <PersonalDetails write={getWritePermission("Complete Profile")} />
                                                        )}

                                                        {activeProfileStep === "Academic Results" && (
                                                            <AcademicResults write={getWritePermission("Complete Profile")} />
                                                        )}

                                                        {activeProfileStep === "Test Results" && (
                                                            <TestResults write={getWritePermission("Complete Profile")} />
                                                        )}

                                                        {activeProfileStep === "Uploaded Documents" && (
                                                            <UploadedDocuments write={getWritePermission("Complete Profile")} />
                                                        )}
                                                    </div>
                                                </div>
                                            )}

                                            {section.id === "Tasks" && (
                                                <div className="complete-profile-section ml-12">
                                                    {/* Progress Steps Bar */}
                                                    <div className="relative flex w-full">
                                                        <div className="relative w-full max-w-3xl">
                                                            <div className="absolute top-2 left-[5%] right-[5%] border-t-2 border-dotted border-[#3483BE]" />
                                                            <div className="relative flex justify-between w-full">
                                                                {taskProgressSteps.map((step, index) => (
                                                                    <div
                                                                        key={index}
                                                                        className="flex flex-col items-center  cursor-pointer"
                                                                        onClick={() => setActiveTaskStep(step.label)}
                                                                    >
                                                                        <div
                                                                            className={`w-5 h-5 rounded-full border-2 flex items-center justify-center 
                        ${step.label === activeTaskStep
                                                                                    ? "border-[#3483BE] bg-white"
                                                                                    : index < taskProgressSteps.findIndex(s => s.label === activeTaskStep)
                                                                                        ? "border-[#3483BE] bg-[#3483BE]"
                                                                                        : "border-[#3483BE] bg-white"
                                                                                }`}
                                                                        >
                                                                            {step.label === activeTaskStep && (
                                                                                <div className="w-3 h-3 rounded-full bg-gradient-to-t from-[#3483BE] to-[#02385E]"></div>
                                                                            )}
                                                                        </div>
                                                                        <span className="text-xs mt-2 text-center w-24 text-gray-600">{step.label}</span>
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        </div>
                                                    </div>
                                                    {/* Content for each step */}
                                                    <div className="mt-8">
                                                        {activeTaskStep === "Student Tasks" && (
                                                            <Tasks write={getWritePermission("Tasks")} />
                                                        )}
                                                        {activeTaskStep === "My Tasks" && (
                                                            <MyTasks write={getWritePermission("Tasks")} />
                                                        )}
                                                    </div>
                                                </div>
                                            )}

                                            {/* History Section */}
                                            {section.id === "History" && (
                                                <div className="flex flex-col md:flex-row w-full gap-4">
                                                    <div className="w-full md:w-1/2 border-gray-200">
                                                        <CallLogs />
                                                    </div>
                                                    <div className="w-full md:w-1/2">
                                                        <FollowUps />
                                                    </div>
                                                </div>
                                            )}

                                            {/* registarion section */}
                                            {section.id === "Registration" && (
                                                <Registrations write={getWritePermission('Registration')} />
                                            )}

                                            {/* UniversityPayment section */}

                                            {section.id === "University Payout" && (
                                                <UniversityPayment write={getWritePermission('University Payout')} />
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )
                    })}
            </div>
        </div>
    );
};

export default StudentDetails;